<script lang="ts">
  import Pause from '@svg/Pause.svelte';
  import Play from '@svg/Play.svelte';
  import { Accordion, AccordionItem, Button, FormGroup, NumberInput } from 'carbon-components-svelte';
  import { onDestroy } from 'svelte';

  import type { AttributeOptions, TestingAttributes, TestingQueryAttributes } from '../modules/attributes';

  type Props = {
    attributes: AttributeOptions;
    testingQueryAttributes: TestingQueryAttributes;
    testingAttributes: TestingAttributes;
  };

  const {
    attributes,
    testingQueryAttributes,
    testingAttributes = {
      seekTest: false,
      seekCurrentInterval: 0,
      seekIntervalId: null,
      seekCurrentIntervalId: null,
    },
  }: Props = $props();

  let isSeekbarFound = $state(true);

  onDestroy(() => {
    resetIntervals();
  });

  function seekbarMove(seekbar: HTMLInputElement) {
    const mouseDownEvent = new MouseEvent('mousedown', {});
    // Dispatch the mousedown event to the element that has the listener
    seekbar.dispatchEvent(mouseDownEvent);

    seekbar.value = `${parseInt(seekbar.max) * Math.random()}`;
    const inputEvent = new Event('input', {});

    seekbar.dispatchEvent(inputEvent);

    const mouseUpEvent = new MouseEvent('mouseup', {});

    // Dispatch mouseup to terminate the process
    seekbar.dispatchEvent(mouseUpEvent);
  }

  function resetIntervals() {
    if (testingAttributes.seekIntervalId) {
      clearInterval(testingAttributes.seekIntervalId);
    }
    if (testingAttributes.seekCurrentIntervalId) {
      clearInterval(testingAttributes.seekCurrentIntervalId);
    }

    testingAttributes.seekCurrentInterval = 0;
    testingAttributes.seekIntervalId = null;
    testingAttributes.seekCurrentIntervalId = null;
  }

  function handleSeekbarTest(initial?: boolean) {
    const seekbar: Nullish<HTMLInputElement> = document
      ?.querySelector?.('iframe')
      ?.contentWindow?.document?.querySelector?.('#seekbar');

    if (!seekbar) {
      isSeekbarFound = false;
      testingAttributes.seekTest = false;
      resetIntervals();
      return;
    }

    const { seekDuration, seekDurationMax, seekDurationMin } = testingQueryAttributes;
    isSeekbarFound = true;
    testingAttributes.seekCurrentInterval = initial
      ? 0
      : (seekDuration ?? Math.floor(Math.random() * (seekDurationMax - seekDurationMin + 1)) + seekDurationMin);

    countdownTimer();
    testingAttributes.seekIntervalId = setTimeout(() => {
      seekbarMove(seekbar);
      if (testingAttributes.seekIntervalId) {
        clearInterval(testingAttributes.seekIntervalId);
      }
      if (testingAttributes.seekCurrentIntervalId) {
        clearInterval(testingAttributes.seekCurrentIntervalId);
      }
      handleSeekbarTest(false);
    }, testingAttributes.seekCurrentInterval * 1000);
  }

  function countdownTimer() {
    if (testingAttributes.seekIntervalId && testingAttributes.seekCurrentInterval) {
      testingAttributes.seekCurrentInterval -= 1;
    } else {
      testingAttributes.seekCurrentInterval = 0;
    }
    const seekCountLabel = document?.querySelector?.('#test-seek__count');
    if (seekCountLabel) {
      seekCountLabel.innerHTML = `${testingAttributes.seekCurrentInterval}`;
    }
    if (testingAttributes.seekIntervalId) {
      testingAttributes.seekCurrentIntervalId = setTimeout(countdownTimer, 1000);
    }
  }
</script>

<Accordion size="xl">
  <AccordionItem open title="Testing Section">
    <FormGroup
      message={!isSeekbarFound}
      messageText={'Seekbar could not be found, try iframe embed methods'}
      legendText="Automated Seek Changing (looping will be enabled)">
      <div
        style="margin-bottom: 16px;width:100%;display:flex;flex-flow: column nowrap;align-items:flex-start;gap:16px;">
        <div style="width:100%;display:flex;flex-flow: row nowrap;align-items:center;gap:16px;">
          <Button
            icon={Play}
            iconDescription="Start Seek Test"
            size="small"
            kind="ghost"
            isSelected={testingAttributes.seekTest}
            onclick={() => {
              attributes.loop = true;
              testingAttributes.seekTest = true;
              handleSeekbarTest(true);
            }} />
          <Button
            icon={Pause}
            iconDescription="Pause Seek Test"
            size="small"
            kind="ghost"
            isSelected={!testingAttributes.seekTest}
            onclick={() => {
              testingAttributes.seekTest = false;
              resetIntervals();
            }} />
          <div>
            {#if testingAttributes.seekIntervalId}
              <span id="test-seek__count-label">{`Time before next seekbar change: `}</span>
              <span id="test-seek__count">{testingAttributes.seekCurrentInterval}</span>
              <span id="test-seek__count-unit">s</span>
            {/if}
          </div>
        </div>
        <div style="display:flex;flex-flow: row nowrap;align-items:center;gap:16px;">
          <NumberInput
            allowEmpty
            data-testid="test-seek__duration"
            size="sm"
            helperText="Fixed duration (seconds) between seekbar changes (default random)"
            bind:value={testingQueryAttributes.seekDuration}
            placeholder="Seek Duration in (seconds)"
            min={0} />
          <NumberInput
            data-testid="test-seek__min"
            size="sm"
            helperText="Minimum duration (seconds) between seekbar changes"
            invalidText="Check value is lower than max"
            max={testingQueryAttributes.seekDurationMax - 1}
            bind:value={testingQueryAttributes.seekDurationMin} />
          <NumberInput
            data-testid="test-seek__max"
            size="sm"
            helperText="Maximum duration (seconds) between seekbar changes"
            invalidText="Check value is higher than min"
            min={testingQueryAttributes.seekDurationMin + 1}
            bind:value={testingQueryAttributes.seekDurationMax} />
        </div>
      </div>
    </FormGroup>
  </AccordionItem>
</Accordion>

<style>
</style>
